import { useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ISupportSession,
  ISupportHookState,
  ISupportHookActions,
} from "../types/support";
import { supportService } from "../services/ai/supportService";
import { SUPPORT_EMAIL } from "../constants/support";
import { useUserActivity } from "../hooks/useUserActivity";

/**
 * Координатор поддержки: ОДНА сессия, ОДИН счётчик попыток AI и ОДИН сигнал
 * эскалации на весь экран.
 *
 * До 31.08.2026 хук вёл собственный чат (`messages`, `sendMessage`,
 * `escalateToHuman`), а `SupportWidget` — свой, со своей `sessionId`. Оба
 * монтировались рядом в `SupportWrapper`, и получалось так:
 *   • открытие чата создавало в БД сессию, которая не получала ни одного
 *     сообщения (человек писал в сессию виджета);
 *   • пункт «связаться с оператором» у лаунчера эскалировал именно эту пустую
 *     сессию, а не переписку, которую человек видел перед собой;
 *   • `aiAttempts` рос только внутри `sendMessage` хука, которого виджет не
 *     вызывал, поэтому `canRequestOperator` навсегда оставался `false` и ворота
 *     ТЗ §1.1 («оператор только после трёх ответов AI») не открывались ни разу,
 *     а собственная кнопка виджета не гейтилась вовсе.
 * Чат остался ровно один — в виджете. Здесь живёт только то, что обязано быть
 * общим для лаунчера и виджета.
 */
export interface UseSupportReturn
  extends ISupportHookState,
    ISupportHookActions {}

export const useSupport = (): UseSupportReturn => {
  const location = useLocation();
  const { t, i18n } = useTranslation("support");
  const { recentActions, trackAction } = useUserActivity();

  const [isOpen, setIsOpen] = useState(false);
  const [currentSession, setCurrentSession] = useState<
    ISupportSession | undefined
  >();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  // Счётчик ответов AI в текущей сессии. Оператор (L2) доступен ТОЛЬКО после
  // 3 ответов AI, не разрешивших вопрос (ТЗ §1.1). Инкрементирует виджет —
  // единственное место, где сообщения реально ходят.
  const [aiAttempts, setAiAttempts] = useState(0);
  // Лаунчер не умеет вести диалог: его пункт «оператор» поднимает флаг, а
  // эскалацию выполняет виджет — в той же сессии и с той же лентой сообщений.
  const [escalationRequested, setEscalationRequested] = useState(false);
  // Текст, с которым чат открыли снаружи (быстрое действие мобильной навигации).
  // Отправляет его виджет, когда лента готова.
  const [pendingInitialMessage, setPendingInitialMessage] = useState<
    string | undefined
  >();
  // Непрочитанное. Канала доставки при закрытом окне нет (ни WebSocket, ни
  // поллинга — в модуле `ai-support` бэкенда шлюза не существует), поэтому
  // единственный реальный случай один: человек отправил вопрос и закрыл окно,
  // а ответ AI пришёл уже после. Его и считаем — выдумывать очередь неоткуда.
  const [unreadCount, setUnreadCount] = useState(0);

  /**
   * Контекст сессии. Бэкенд принимает его вложенным объектом
   * (`SupportSessionContextDto`), и состав полей там закрытый: лишнее поле —
   * это 400, а не «молча проигнорируется».
   */
  const getSessionContext = useCallback(() => {
    return {
      currentPage: location.pathname,
      referrer: document.referrer,
      lastActions: recentActions.map(
        (action) => `${action.type}:${action.meta?.["id"] ?? action.timestamp}`,
      ),
      deviceInfo: {
        type:
          window.innerWidth < 768
            ? ("mobile" as const)
            : window.innerWidth < 1024
              ? ("tablet" as const)
              : ("desktop" as const),
        os: navigator.platform,
        browser: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    };
  }, [location.pathname, recentActions]);

  /**
   * Сессия создаётся ЛЕНИВО — при первом сообщении, а не при открытии окна.
   * Прежде она создавалась на каждое открытие чата, и каждое открытие без
   * единой реплики оставляло в `support_sessions` пустую строку.
   */
  const ensureSession = useCallback(async (): Promise<string | null> => {
    if (currentSession) return currentSession.id;

    try {
      setIsLoading(true);
      setError(undefined);
      const session = await supportService.createSession(
        getSessionContext(),
        i18n.language,
      );
      setCurrentSession(session);
      return session.id;
    } catch (err) {
      console.error("Error creating support session:", err);
      setError(t("errors.openFailed", { email: SUPPORT_EMAIL }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [currentSession, getSessionContext, i18n.language, t]);

  const openSupport = useCallback(
    (initialMessage?: string) => {
      setIsOpen(true);
      setError(undefined);
      setUnreadCount(0);
      if (initialMessage) {
        setPendingInitialMessage(initialMessage);
      }
      trackAction("support_open", {
        hasInitialMessage: Boolean(initialMessage),
      });
    },
    [trackAction],
  );

  const consumeInitialMessage = useCallback(() => {
    setPendingInitialMessage(undefined);
  }, []);

  const closeSupport = useCallback(async () => {
    if (currentSession) {
      try {
        await supportService.closeSession(currentSession.id);
      } catch (err) {
        console.error("Error closing support session:", err);
      }
    }

    setIsOpen(false);
    setCurrentSession(undefined);
    setAiAttempts(0);
    setUnreadCount(0);
    setEscalationRequested(false);
    setPendingInitialMessage(undefined);
    setError(undefined);
    trackAction("support_close");
  }, [currentSession, trackAction]);

  /** Один ответ AI = одна попытка линии L1 (ТЗ §1.1). Зовёт виджет. */
  const registerAiReply = useCallback(() => {
    setAiAttempts((prev) => prev + 1);
    if (!isOpen) {
      setUnreadCount((prev) => prev + 1);
    }
  }, [isOpen]);

  /**
   * Запрос эскалации от лаунчера: открываем окно и поднимаем флаг. Сам вызов
   * `escalateSession` делает виджет — иначе эскалация уходила бы в сессию, в
   * которой нет ни одного сообщения, и человек не увидел бы ответа в ленте.
   */
  const requestEscalation = useCallback(() => {
    setIsOpen(true);
    setEscalationRequested(true);
    trackAction("support_escalate_request");
  }, [trackAction]);

  const acknowledgeEscalation = useCallback(() => {
    setEscalationRequested(false);
  }, []);

  return {
    isOpen,
    ...(currentSession != null && { currentSession }),
    sessionId: currentSession?.id ?? null,
    isLoading,
    ...(error != null && { error }),
    ...(pendingInitialMessage != null && { pendingInitialMessage }),
    escalationRequested,
    unreadCount,
    // Ворота L2 (ТЗ §1.1): оператор — после трёх ответов AI.
    canRequestOperator: aiAttempts >= 3,

    openSupport,
    closeSupport,
    ensureSession,
    registerAiReply,
    requestEscalation,
    acknowledgeEscalation,
    consumeInitialMessage,
  };
};
